import { Button } from '@/components/ui/button';
import { Drawer, DrawerContent, DrawerDescription, DrawerFooter, DrawerTitle } from '@/components/ui/drawer';
import { useClientTranslation } from '@/lib/core';

export interface DateUnavailableSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The day the member came in with (Events Calendar), already formatted for display. */
  dateLabel: string;
  /** Opens the "When" picker, which only offers the dates still bookable. */
  onChooseAnotherDate: () => void;
}

/**
 * Told on arrival when the day the member tapped in the Events Calendar can no longer be booked
 * (sold out, or no date on that day any more). The flow never swaps in another date on its own —
 * the nearest one is not necessarily the one the member wants — so the only way on is to choose.
 * Same shell as the ELP's `SoldOutEventSheet`.
 */
export function DateUnavailableSheet({
  open,
  onOpenChange,
  dateLabel,
  onChooseAnotherDate,
}: DateUnavailableSheetProps) {
  const t = useClientTranslation();

  return (
    <Drawer open={open} onOpenChange={onOpenChange} showSwipeHandle={false}>
      <DrawerContent className="bg-[#1a1a1a]">
        <div className="flex flex-1 flex-col gap-4 px-4 pt-10 pb-4">
          <DrawerTitle className="font-pp-neue-montreal text-[24px] leading-tight font-semibold text-white">
            {t('App.Events.DateUnavailableTitle')}
          </DrawerTitle>
          <DrawerDescription className="font-pp-neue-montreal text-[16px] leading-normal tracking-[0.25px] text-[#eaeaea]">
            {t('App.Events.DateUnavailableDescription', { date: dateLabel })}
          </DrawerDescription>
        </div>

        <DrawerFooter className="flex shrink-0 flex-col gap-2 border-t border-[#c8c8c8]/30 px-4 pt-6 pb-10">
          <Button
            variant="primary-solid"
            palette="white"
            size="lg"
            className="w-full"
            onClick={() => {
              onOpenChange(false);
              onChooseAnotherDate();
            }}
          >
            {t('App.Events.ChooseAnotherDate')}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
